import { useEffect, useCallback, useRef } from 'react';
import { useSession } from '@/context/SessionContext';
import { useNotifications } from '@/context/NotificationContext';
import {
  registerServiceWorker,
  requestNotificationPermission,
  playNotificationSound,
  vibrateForNotification,
  setNotificationBadge,
  areNotificationsEnabled,
} from '@/lib/notifications/native';

/**
 * Hook para gestionar notificaciones nativas
 * - Registra Service Worker
 * - Sincroniza notificaciones BD con notificaciones nativas
 * - Maneja sonidos y vibraciones
 * 
 * NOTA: Los permisos se solicitan manualmente cuando el usuario hace click
 * (los navegadores requieren user gesture para solicitar permisos)
 */
export function useNativeNotifications() {
  const { session } = useSession();
  const { notifications, unreadCount } = useNotifications();
  const lastPlayedNotificationIdRef = useRef<string | null>(null);

  // Inicializar Service Worker al cargar (sin solicitar permisos)
  useEffect(() => {
    const initServiceWorker = async () => {
      if (!session?.user?.id) return;

      try {
        // Solo registrar Service Worker (no solicitar permisos aquí)
        await registerServiceWorker();
      } catch (error) {
        console.warn('[useNativeNotifications] SW registration warning:', error);
      }
    };

    initServiceWorker();
  }, [session?.user?.id]);

  // Actualizar badge con contador de no leídas
  useEffect(() => {
    if (!areNotificationsEnabled()) return;

    setNotificationBadge(unreadCount);
  }, [unreadCount]);

  // Escuchar notificaciones nuevas y disparar native notifications
  useEffect(() => {
    if (!areNotificationsEnabled() || !session?.user?.id) return;

    // La última notificación (más reciente)
    const latestNotification = notifications[0];

    if (!latestNotification) return;

    // Primera hidratación: registrar el último ID sin reproducir sonido.
    if (!lastPlayedNotificationIdRef.current) {
      lastPlayedNotificationIdRef.current = latestNotification.id;
      return;
    }

    // Evitar repetir sonido cuando solo se refresca la lista.
    if (lastPlayedNotificationIdRef.current === latestNotification.id) {
      return;
    }

    lastPlayedNotificationIdRef.current = latestNotification.id;

    // Disparar sonido y vibración si es una notificación de orden
    if (latestNotification.type === 'order_update' || latestNotification.type === 'delivery_update') {
      playNotificationSound('chime');
      vibrateForNotification('normal');

      // Log para debugging
    }
  }, [notifications, session?.user?.id]);

  // Función para solicitar permiso manualmente
  const requestPermission = useCallback(async () => {
    const permission = await requestNotificationPermission();
    return permission === 'granted';
  }, []);

  // Función para comprobar si están habilitadas
  const isEnabled = useCallback(() => areNotificationsEnabled(), []);

  return {
    requestPermission,
    isEnabled,
    unreadCount,
  };
}

/**
 * Hook para escuchar cambios específicos de órdenes en tiempo real
 * Dispara notificaciones cuando se asigna un repartidor, está llegando, etc.
 */
export function useOrderNotifications(orderId?: string) {
  const { session } = useSession();
  const { notifications } = useNotifications();
  const lastPlayedOrderNotificationIdRef = useRef<string | null>(null);

  // Filtrar notificaciones relacionadas con esta orden
  const orderNotifications = notifications.filter((n) => {
    if (!orderId) return false;
    const meta = n.meta as any;
    return meta?.order_id === orderId;
  });

  // La última notificación de esta orden
  const latestOrderNotification = orderNotifications[0];

  // Disparar sonido cuando hay cambios
  useEffect(() => {
    if (!latestOrderNotification || !session?.user?.id) return;

    // Primera hidratación: registrar el último ID sin reproducir sonido.
    if (!lastPlayedOrderNotificationIdRef.current) {
      lastPlayedOrderNotificationIdRef.current = latestOrderNotification.id;
      return;
    }

    // Evitar repetir sonido en refrescos/re-renders.
    if (lastPlayedOrderNotificationIdRef.current === latestOrderNotification.id) {
      return;
    }

    lastPlayedOrderNotificationIdRef.current = latestOrderNotification.id;

    const meta = latestOrderNotification.meta as any;

    // Sonido especial para eventos críticos
    if (meta?.event_type === 'courier_arriving' || meta?.event_type === 'cancelled') {
      playNotificationSound('alert');
      vibrateForNotification('strong');
    }

  }, [latestOrderNotification, session?.user?.id, orderId]);

  return {
    orderNotifications,
    latestOrderNotification,
  };
}
